import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';

const languages = [
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'da', label: 'Dansk', flag: '🇩🇰' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
];

export default function Navbar() {
  const { i18n } = useTranslation();
  const location = useLocation();
  const [showCategories, setShowCategories] = useState(false);
  const [showLang, setShowLang] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const catRef = useRef<HTMLDivElement>(null);
  const langRef = useRef<HTMLDivElement>(null);

  const isHome = /^\/(en|fr|da|de)?\/?$/.test(location.pathname);
  const solid = scrolled || !isHome;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (catRef.current && !catRef.current.contains(e.target as Node)) setShowCategories(false);
      if (langRef.current && !langRef.current.contains(e.target as Node)) setShowLang(false);
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setShowCategories(false);
  }, [location.pathname]);

  const changeLang = (code: string) => {
    i18n.changeLanguage(code);
    setShowLang(false);
  };

  const navLinkCls = `px-3 py-2 text-sm font-semibold rounded-md transition-colors whitespace-nowrap ${
    solid ? 'text-foreground-800 hover:text-foreground-950 hover:bg-background-100' : 'text-white/90 hover:text-white hover:bg-white/10'
  }`;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        solid ? 'bg-white/95 backdrop-blur-md shadow-sm border-b border-background-200' : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16 md:h-18">
          {/* Logo */}
          <a href="/" className="flex items-center gap-2 whitespace-nowrap">
            <span className={`text-lg md:text-xl font-bold tracking-tight ${solid ? 'text-foreground-950' : 'text-white'}`}>
              ACB Spreadsheet
            </span>
          </a>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-1">
            <a href="/" className={`${navLinkCls} flex items-center gap-1.5`}>
              <i className="ri-home-4-line" />
              Home
            </a>

            {/* Categories Dropdown */}
            <div className="relative" ref={catRef}>
              <button
                onClick={() => setShowCategories(!showCategories)}
                className={`${navLinkCls} flex items-center gap-1`}
              >
                Categories
                <i className={`ri-arrow-down-s-line transition-transform ${showCategories ? 'rotate-180' : ''}`} />
              </button>
              {showCategories && (
                <div className="absolute top-full left-0 mt-1 w-60 bg-white rounded-lg shadow-lg border border-background-200 py-2 max-h-[70vh] overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-50 hover:text-foreground-950 transition-colors"
                    >
                      <img src={cat.image} alt={cat.name} className="w-7 h-7 rounded object-cover" />
                      <span>{cat.name}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* Buying Guide (highlighted) */}
            <a
              href="/allchinabuy-buying-guide/"
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-md transition-colors whitespace-nowrap"
            >
              <i className="ri-shopping-bag-line" />
              Buying Guide
            </a>

            <a href="/allchinabuy-spreadsheet-guide/" className={navLinkCls}>Guides</a>
            <a href="/allchinabuy-shipping/" className={navLinkCls}>Shipping</a>
            <a href="/allchinabuy-qc/" className={navLinkCls}>QC</a>
            <a href="/allchinabuy-review/" className={navLinkCls}>Reviews</a>
            <a href="/safety/" className={navLinkCls}>Safety</a>
          </div>

          {/* Right side: Language + CTA + Mobile toggle */}
          <div className="flex items-center gap-2">
            {/* Language Switcher */}
            <div className="relative hidden sm:block" ref={langRef}>
              <button
                onClick={() => setShowLang(!showLang)}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-md transition-colors ${solid ? 'text-foreground-800 hover:text-foreground-950 hover:bg-background-100' : 'text-white/90 hover:text-white hover:bg-white/10'}`}
              >
                <i className="ri-global-line" />
                <span>{i18n.language.toUpperCase()}</span>
                <i className={`ri-arrow-down-s-line transition-transform ${showLang ? 'rotate-180' : ''}`} />
              </button>
              {showLang && (
                <div className="absolute top-full right-0 mt-1 w-40 bg-white rounded-lg shadow-lg border border-background-200 py-1">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => changeLang(lang.code)}
                      className={`flex items-center gap-2 w-full px-4 py-2 text-sm transition-colors ${
                        i18n.language === lang.code ? 'text-primary-600 bg-primary-50' : 'text-foreground-700 hover:bg-background-50'
                      }`}
                    >
                      <span className="text-base">{lang.flag}</span>
                      {lang.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Main CTA */}
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="hidden md:inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold bg-primary-500 text-white rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              Buy It Now
              <i className="ri-external-link-line" />
            </a>

            {/* Mobile menu toggle */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className={`lg:hidden p-2 rounded-md ${solid ? 'text-foreground-800 hover:bg-background-100' : 'text-white hover:bg-white/10'}`}
              aria-label="Menu"
            >
              <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`} />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-background-200 shadow-lg max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-4 space-y-1">
            <a href="/" className="flex items-center gap-3 px-3 py-2.5 text-sm font-semibold text-foreground-900 hover:bg-background-50 rounded-lg">
              <i className="ri-home-4-line text-lg" /> Home
            </a>
            <a href="/allchinabuy-buying-guide/" className="flex items-center gap-3 px-3 py-2.5 text-sm font-semibold text-primary-600 hover:bg-primary-50 rounded-lg">
              <i className="ri-shopping-bag-line text-lg" /> Buying Guide
            </a>
            <a href="/allchinabuy-spreadsheet-guide/" className="flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-50 rounded-lg">
              <i className="ri-book-open-line text-lg" /> Guides
            </a>
            <a href="/allchinabuy-shipping/" className="flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-50 rounded-lg">
              <i className="ri-truck-line text-lg" /> Shipping
            </a>
            <a href="/allchinabuy-qc/" className="flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-50 rounded-lg">
              <i className="ri-camera-line text-lg" /> QC
            </a>
            <a href="/allchinabuy-review/" className="flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-50 rounded-lg">
              <i className="ri-star-line text-lg" /> Reviews
            </a>
            <a href="/safety/" className="flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-50 rounded-lg">
              <i className="ri-shield-check-line text-lg" /> Safety
            </a>

            {/* Categories in mobile */}
            <div className="pt-3 mt-3 border-t border-background-200">
              <p className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-foreground-500">
                Categories
              </p>
              <div className="grid grid-cols-2 gap-2 px-3 pb-2">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-2 px-2 py-2 text-sm text-foreground-700 bg-background-50 rounded-lg hover:bg-background-100"
                  >
                    <img src={cat.image} alt={cat.name} className="w-7 h-7 rounded object-cover" />
                    <span className="truncate">{cat.name}</span>
                  </a>
                ))}
              </div>
            </div>

            {/* Language in mobile */}
            <div className="pt-3 mt-3 border-t border-background-200 px-3 pb-2">
              <p className="py-2 text-xs font-semibold uppercase tracking-wider text-foreground-500">
                Language
              </p>
              <div className="grid grid-cols-4 gap-2">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => changeLang(lang.code)}
                    className={`flex flex-col items-center gap-1 py-2 rounded-lg text-xs font-medium transition-colors ${
                      i18n.language === lang.code ? 'bg-primary-50 text-primary-600' : 'bg-background-50 text-foreground-700 hover:bg-background-100'
                    }`}
                  >
                    <span className="text-lg">{lang.flag}</span>
                    <span>{lang.code.toUpperCase()}</span>
                  </button>
                ))}
              </div>
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="mt-3 flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold bg-primary-500 text-white rounded-lg hover:bg-primary-600"
            >
              Buy It Now
              <i className="ri-external-link-line" />
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}